// apps/share-web/src/chart/chart-follow.ts
//
// Плейхед → активный блок чарта: подсветка и ведение листа.
//
// Источник позиции приходит ИНТЕРФЕЙСОМ, а не конкретным плеером. В волне A это
// локальный Player; в волне C — клиент лайв-сессии. Благодаря этому «лайв в
// аудио-шейре» останется проводкой на несколько строк.
//
// Геометрия — тоже интерфейсом. Причина не в чистоте: happy-dom не считает
// раскладку вовсе (getBoundingClientRect и clientWidth дают нули), и без шва
// замки 1 и 2 недоказуемы, потому что ветка прокрутки не исполнится никогда.
//
// Три замка, каждый — дефект, найденный живым прогоном прототипа:
//  1. нулевая ширина = лист скрыт или ещё раздвигается; строки переносятся
//     иначе, значит и вертикальные позиции другие. Считать в этот момент —
//     увезти лист в пустоту (замер: scrollTop 1346 при содержимом на 900).
//  2. цель прокрутки — по РАЗНИЦЕ ПРЯМОУГОЛЬНИКОВ, не по offsetTop: лист не
//     является offsetParent'ом блоков, и offsetTop включает высоту шапки
//     (замер: припев обрезался сверху на 111 px). Это зашито в реализацию
//     `offsetOf` ниже.
//  3. пока панель едет — красим, но не прокручиваем (замер: 362 → 924 → 414,
//     рывок вперёд и откат).

import { mapSectionsToBlocks, followTargets } from '@bandway/chordpro';

export interface SectionSec { id: string; name: string; startSec: number; endSec: number; }

/** Откуда берётся позиция. Волна A — локальный плеер, волна C — лайв. */
export interface PositionSource {
  now(): { songIdx: number; posSec: number; playing: boolean } | null;
}

/** Шов геометрии. В проде читает DOM, в тестах — подставляется. */
export interface Viewport {
  /** 0 = лист скрыт или ещё едет (замок 1). */
  width(): number;
  height(): number;
  scrollTop(): number;
  /** Смещение блока в единицах прокрутки листа (замок 2). */
  offsetOf(el: HTMLElement): number;
  scrollTo(top: number): void;
}

export interface ChartFollowDeps {
  pane: HTMLElement;
  sections: SectionSec[];
  /**
   * Хранимые связи «id секции → заголовок как написан» из нагрузки
   * (`chart.links`). Отсутствие — законное состояние: так выглядит шейр,
   * опубликованный до того, как поле поехало на провод, и тогда связь
   * выводится по именам. Решает это ядро, не эта поверхность.
   */
  links?: Record<string, string> | null;
  position: PositionSource;
  viewport: Viewport;
  /**
   * Лид ведения в СЕКУНДАХ (`leadSeconds(bpm, timeSignature)` из ядра).
   *
   * Необязательное и по умолчанию отсутствует: опубликованная до этой волны
   * ссылка не меняет поведение сама по себе, а песня без темпа (bpm: 0 в обоих
   * контрактах шейров) даёт 0 — то есть ровно сегодняшнее ведение.
   */
  leadSec?: number;
}

export interface ChartFollow {
  /**
   * Один синхронный кадр.
   *
   * Кадром ведение НЕ владеет, и это утверждение интерфейса, а не умолчание.
   * Своего rAF здесь был `start()`/`stop()`, и к концу волны A.1 его не звал
   * никто: и панель, и полный экран держат ОДИН кадр на поверхность и тикают им
   * и ведение, и шапку. Пара осталась бы ловушкой ровно того вида, из-за
   * которого правило и появилось — следующая поверхность позвала бы
   * `start()` как выглядящий уместным API и получила бы двойной тик каждый
   * кадр. Поверхность заводит кадр, поверхность его и отменяет.
   */
  tickOnce(): void;
  /** Панель раздвигается — прокрутку держим до конца перехода (замок 3). */
  setAnimating(on: boolean): void;
  /** Читатель повёл лист сам: молчим до следующей смены секции. */
  handOff(): void;
  /** Лист перерисован (сменилась тональность или число колонок). */
  invalidate(): void;
  /**
   * Читатель ткнул в секцию (лента структуры, words page v2): лист едет к её
   * блоку и ОСТАЁТСЯ там до следующей смены лид-секции — как после ручной
   * прокрутки (handOff). Секция без блока (безымянная, не связанная) — ничего.
   */
  scrollToSection(sectionIdx: number): void;
}

/** Прод-реализация шва. Замок 2 живёт здесь. */
export function domViewport(pane: HTMLElement): Viewport {
  return {
    width: () => pane.clientWidth,
    height: () => pane.clientHeight,
    scrollTop: () => pane.scrollTop,
    offsetOf: (el) => {
      const pr = pane.getBoundingClientRect();
      const tr = el.getBoundingClientRect();
      return pane.scrollTop + (tr.top - pr.top);
    },
    scrollTo: (top) => pane.scrollTo({ top: Math.max(0, top), behavior: 'smooth' }),
  };
}

/** Доля высоты листа, выше которой встаёт текущая секция. */
const LEAD = 0.2;

/**
 * Секция расписания, внутри которой стоит позиция, или −1 — «вне расписания»
 * (до первой секции, в дыре между ними, за последней). Начало секции ей
 * принадлежит, конец — уже нет: иначе на стыке две секции считались бы
 * текущими одновременно.
 *
 * Экспортируется потому, что этот вопрос задают ТРИ потребителя: ведение листа
 * (ниже), строка «сейчас → дальше» в полном экране и она же в панели
 * (chart-now-next.ts). До этого копия жила приватно в chart-fullscreen.ts —
 * TD-260828-section-at-duplicated-in-two-web-modules, и её шапка прямым текстом
 * называла правильный конец: «вынести sectionIndexAt(sections, posSec)
 * экспортом в chart-follow.ts и звать из обоих мест». Расхождение границ между
 * копиями означало бы, что шапка называет одну секцию, а лист подсвечивает
 * другую.
 */
export function sectionIndexAt(sections: readonly SectionSec[], posSec: number): number {
  for (let i = 0; i < sections.length; i++) {
    if (posSec >= sections[i].startSec && posSec < sections[i].endSec) return i;
  }
  return -1;
}

export function createChartFollow(deps: ChartFollowDeps): ChartFollow {
  const { pane, sections, links, position, viewport, leadSec = 0 } = deps;

  let animating = false;
  let handedOff = false;
  // Секция ЛИДА, а не звучащая: возврат ведения привязан к её смене (см.
  // tickOnce), и обещание handOff() живёт в той же оси — иначе оно отменялось
  // бы событием из другой системы отсчёта.
  let lastLeadSection = -2;
  // Три независимых счётчика на три цели — «сейчас», «дальше», цель прокрутки
  // — как на десктопе (chart-follow-desktop.js). Одним не обойтись: переход
  // «метка сменилась, а „сейчас“ нет» случается за два такта до КАЖДОЙ
  // границы, и общий счётчик оставил бы одну из целей неперекрашенной.
  let lastBlock = -2;
  let lastNextBlock = -2;
  let lastScrollBlock = -2;
  let cache: { blocks: HTMLElement[]; secToBlock: (HTMLElement | null)[] } | null = null;

  /**
   * Секция расписания → блок листа. Карта, а не поиск на каждый кадр.
   *
   * Сравнивать имена строкой здесь неверно в самом основании: аранжировка
   * НУМЕРУЕТ секции (`Chorus 1`, `Chorus 2`), а документ — нет, припев написан
   * один раз под `[Chorus]`. Связь берётся из ядра тем же `linkTargets`, что и
   * на десктопе (`chart-read.js`): первая ступень — точное совпадение ключа
   * после нормализации (`PRE CHORUS` = `Pre-Chorus`), вторая — по семье
   * (`Chorus` → `Chorus 1`, `Chorus 2`).
   *
   * `linkTargets` зовётся с ПОЛНЫМ списком секций, по одному вызову на блок:
   * его ступени глобальны — нашлось точное совпадение, и семья уже не
   * рассматривается (`linkTargets('Chorus', ['Chorus','Chorus 1'])` даёт только
   * `['Chorus']`). По одной секции за раз ответ был бы другим.
   *
   * Метка блока — заголовок, а если его нет — указатель. У уцелевшего
   * `{chorus}` (припева в документе нет вовсе) стоит только `.cw-ref`, и такой
   * блок тоже ведётся: подсветка — это утверждение о том, где песня СЕЙЧАС, и
   * оставить её на прошлом блоке значит утверждать «мы в Verse 3», пока
   * команда играет припев. «Мы здесь, и слов на это в документе нет» —
   * неприятно, но правда.
   */
  const buildCache = (): { blocks: HTMLElement[]; secToBlock: (HTMLElement | null)[] } => {
    const blocks = [...pane.querySelectorAll<HTMLElement>('.cw-blk')];

    // Сопоставление считает ЯДРО (`mapSectionsToBlocks`), а не этот файл.
    // Прежде здесь лежала своя копия правила поверх `linkTargets`, и она не
    // знала про ХРАНИМЫЕ связи — а русский чарт только ими и держится
    // («Куплет 1» ≠ «Verse 1»), поэтому зал следил не за тем блоком: замер на
    // настоящей песне дал 7 расхождений с ведущим из 25 секций.
    //
    // Порядок секций ядру важен (оно раздаёт копии сверху вниз), и он
    // обеспечен на входе — `gridSections` сортирует по времени.
    //
    // Уцелевшая отсылка `{chorus}` едет как ЕСТЬ, флагом `ref`: правило «вести
    // её наравне с заголовком» с 03.09 живёт в ядре и одинаково на обеих
    // поверхностях. Прежде здесь стояла местная поправка — расхождение с
    // десктопом, снятое решением Сергея.
    const descriptors = blocks.map((b) => {
      const head = b.querySelector('.cw-head');
      if (head) return { name: head.textContent ?? '', ref: false };
      const ref = b.querySelector('.cw-ref');
      return ref ? { name: ref.textContent ?? '', ref: true } : { name: null, ref: false };
    });

    const mapped = mapSectionsToBlocks(sections, descriptors, links);
    const secToBlock = mapped.map((bi) => (typeof bi === 'number' && bi >= 0 ? blocks[bi] ?? null : null));
    return { blocks, secToBlock };
  };

  // Кэш зависит от DOM листа, а он перерисовывается при смене тональности и
  // числа колонок — потому строится лениво и сбрасывается в invalidate(), а не
  // считается каждый кадр. Список блоков живёт тут же, а не только связка
  // секция→блок: `tickOnce` адресует блоки и по индексам из `followTargets`
  // (`nowBlock`/`nextBlock`/`scrollBlock`), и повторный запрос `.cw-blk` из DOM
  // на каждый кадр был бы той же лишней работой, которую кэш и снимает.
  const ensureCache = (): { blocks: HTMLElement[]; secToBlock: (HTMLElement | null)[] } => {
    if (!cache) cache = buildCache();
    return cache;
  };

  const blockFor = (sectionIdx: number): HTMLElement | null => ensureCache().secToBlock[sectionIdx] ?? null;

  /**
   * Индекс блока (`data-i`) по индексу секции, или −1 — контракт, которого
   * ждёт `followTargets` (ядро терпит и `null`, но здесь есть настоящий
   * числовой ответ, и превращать его в невалидное значение незачем).
   */
  const blockIndexFor = (sectionIdx: number): number => {
    const el = sectionIdx < 0 ? null : blockFor(sectionIdx);
    return el ? Number(el.dataset.i) : -1;
  };

  const tickOnce = (): void => {
    // ЗАМОК 1. Ширины нет — считать нечего и незачем.
    if (viewport.width() <= 0) return;

    const at = position.now();
    if (!at) return;

    const si = sectionIndexAt(sections, at.posSec);
    // Секция ЛИДА — та, что заиграет через `leadSec` секунд считая от СЕЙЧАС.
    // Без темпа (`leadSec` отсутствует или 0, см. деструктуризацию deps) она
    // равна звучащей, и вся арифметика ниже схлопывается в сегодняшнее
    // ведение байт-в-байт — этим и держится обратная совместимость контракта,
    // ещё не проведённого до поверхностей (задача 7).
    const sl = leadSec > 0 ? sectionIndexAt(sections, at.posSec + leadSec) : si;

    let returning = false;
    // Возврат ведения — на смене ЛИД-секции, а не звучащей. Тот же приём, что
    // на десктопе (chart-follow-desktop.js, tickOnce): смена si вернула бы
    // лист ровно в момент, когда он и так обязан быть впереди — метка «дальше»
    // уже стоит, а прокрутка ещё держится на прошлом блоке. Не таймер: лист
    // возвращается сам, когда лид ушёл на новый кусок.
    if (sl !== lastLeadSection) {
      lastLeadSection = sl;
      if (handedOff) { handedOff = false; returning = true; }
    }

    const { blocks } = ensureCache();
    // БЛОКА МОЖЕТ НЕ БЫТЬ — и тогда подсветку надо СНЯТЬ, а не оставить на
    // прошлом. Два случая, и оба законные: позиция вне расписания (до первой
    // секции, в дыре, за последней) и позиция внутри БЕЗЫМЯННОЙ секции, у
    // которой блока нет по построению (buildCache выше). Правило «когда метки
    // „дальше“ нет» — из ядра (followTargets) и не повторено здесь: то же
    // правило считает десктоп, и разойтись оно обязано именно в переписанной
    // копии, а не тут.
    //
    // Прежде здесь стоял ранний выход `if (si < 0) return;`, и он превращал
    // «блока нет» в «блок прежний»: девять секунд безымянного проигрыша лист
    // уверенно подсвечивал Verse 1, а потом рывком прыгал на CHORUS (замер
    // живого стенда — `gridSections` в chart-song.ts). Подсвеченный Verse 1 во
    // время проигрыша — ложное утверждение «мы здесь», и оно ХУЖЕ отсутствия
    // подсветки: лист без подсветки читается как «мы между кусками», лист с
    // чужой подсветкой — как «я смотрю не туда».
    const t = followTargets(si, sl, blockIndexFor);
    const elAt = (bi: number): HTMLElement | null => (bi >= 0 ? blocks[bi] ?? null : null);
    const nowEl = elAt(t.nowBlock);
    const nextEl = elAt(t.nextBlock);
    const scrollEl = elAt(t.scrollBlock);

    if (t.nowBlock !== lastBlock) {
      lastBlock = t.nowBlock;
      // Подсвечивается ТОЛЬКО текущее место. Пройденное не притушивается:
      // «не стоит делать затемнения прошлых припевов, потому что просто
      // подсвечивать текущее место» (Сергей, живой прогон). На десктопе такого
      // никогда и не было, и chart-rail.css объясняет почему: по копии припева
      // ПОЮТ — притушенный текст с ней не прочтёшь. Здесь притушивание к тому
      // же било по будущему тексту при перемотке назад.
      for (const el of blocks) el.classList.toggle('is-now', el === nowEl);
    }
    // Метка «дальше» — ТИХАЯ и СВОЯ, отдельным классом и отдельным счётчиком:
    // амбровое «сейчас» обязано остаться на РЕАЛЬНО играющей секции (см. выше
    // про ложное «мы здесь»), и две цели не могут делить один класс.
    if (t.nextBlock !== lastNextBlock) {
      lastNextBlock = t.nextBlock;
      for (const el of blocks) el.classList.toggle('is-next', el === nextEl);
    }

    // Цель прокрутки — СВОЙ счётчик, отдельный от подсветки: пока метка есть,
    // лист ведут к ней, а «сейчас» стоит на реально играющем блоке.
    const scrollChanged = t.scrollBlock !== lastScrollBlock;
    if (scrollChanged) lastScrollBlock = t.scrollBlock;
    // Выход смотрит на ЦЕЛЬ ПРОКРУТКИ, а не на «сейчас»: играет безымянная
    // секция (блока нет, `nowEl` тоже нет), а лид уже указывает на связанную —
    // вести есть куда, хотя красить нечего. Ранний выход по `nowEl` оставил бы
    // лист там, куда он уже уехал за прошлую границу — ровно тот проигрыш,
    // ради которого лид и написан.
    if (!scrollEl) return;

    // ЗАМОК 3.
    if (animating || handedOff) return;
    // Ведём, когда сменилась ЦЕЛЬ ПРОКРУТКИ ЛИБО когда ведение только что
    // вернулось на смене лид-секции. Единственный ранний выход «цель та же —
    // уходим» гасил бы два разных вопроса одним ответом: «перекрашивать ли»
    // (нечего, и правильно) и «возвращать ли прокрутку» (надо). Соседние
    // секции, стоящие за ОДНИМ блоком — припев, написанный один раз и играемый
    // дважды подряд, — снимали бы handedOff и уходили молча, оставляя лист
    // там, куда его увёл палец. То есть правило «ручная прокрутка отдаёт
    // ведение до следующей смены секции» отказывало бы ровно в том месте, ради
    // которого механизм и написан.
    if (!scrollChanged && !returning) return;
    viewport.scrollTo(viewport.offsetOf(scrollEl) - viewport.height() * LEAD);
  };

  return {
    tickOnce,
    setAnimating: (on) => {
      animating = on;
      if (!on) { lastBlock = -2; lastNextBlock = -2; lastScrollBlock = -2; }
    },
    handOff: () => { handedOff = true; },
    // Счётчики целей сбрасываются, чтобы подсветка, метка «дальше» и цель
    // прокрутки перекрасились/адресовались заново на НОВЫХ элементах —
    // прежние из DOM ушли. `lastLeadSection` НЕ сбрасывается намеренно: он
    // читается как «лид-секция сменилась», а перерисовка листа сменой секции
    // не является, и ведение, снимая на ней handedOff, возвращалось бы само —
    // то есть обещание handOff() («молчим до следующей смены секции»)
    // отменялось бы событием, которого не было. То же соображение у
    // setAnimating(false) выше: он тоже трогает только счётчики целей.
    invalidate: () => { cache = null; lastBlock = -2; lastNextBlock = -2; lastScrollBlock = -2; },
    scrollToSection: (sectionIdx) => {
      const el = sectionIdx < 0 ? null : blockFor(sectionIdx);
      if (!el) return;
      // Тот же отступ сверху, что у ведения: блок ложится туда же, куда его
      // положил бы кадр, а не впритык к краю.
      viewport.scrollTo(viewport.offsetOf(el) - viewport.height() * LEAD);
      handedOff = true;
    },
  };
}
